import React, { useState, useEffect } from 'react';
import * as challengesApi from '../utils/challengesApi';
import DifficultySelector from './DifficultySelector';

// Sport name mapping to backend values
const SPORT_MAPPING = {
  'Football': 'Football',
  'Course': 'Running',
  'Natation': 'Swimming',
  'Basketball': 'Basketball',
  'Tennis': 'Tennis',
  'Cyclisme': 'Cycling',
  'autre': null
};

// Metric type mapping by sport
const METRIC_BY_SPORT = {
  'Football': 'goals',
  'Running': 'distance',
  'Swimming': 'distance',
  'Basketball': 'points',
  'Tennis': 'matchesWon',
  'Cycling': 'distance',
  null: 'eventsJoined'
};

export default function ChallengeForm({ initialData = {}, onSaved, onCancel, onPreviewChange }) {
  const [title, setTitle] = useState(initialData.title || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [sportType, setSportType] = useState(initialData.sportType || '');
  const [difficulty, setDifficulty] = useState(initialData.difficulty || 'easy');
  const [targetValue, setTargetValue] = useState(initialData.targetValue || 1);
  const [rewardXP, setRewardXP] = useState(initialData.rewardXP || 0);
  const [duration, setDuration] = useState(initialData.duration || 7);
  const [durationUnit, setDurationUnit] = useState('weekly');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const preview = { title, difficulty, rewardXP, duration, sportType, targetValue };
    onPreviewChange && onPreviewChange(preview);
  }, [title, difficulty, rewardXP, duration, sportType, targetValue, onPreviewChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    // Map sport name to backend value
    const mappedSportType = SPORT_MAPPING[sportType] || null;
    
    // Get metric type based on sport
    const metricType = METRIC_BY_SPORT[mappedSportType];
    
    // Calculate dates
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + duration);
    
    const payload = {
      title,
      description,
      sportType: mappedSportType,
      difficulty,
      targetValue,
      metricType,
      rewardXP,
      duration: durationUnit,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    
    try {
      let res;
      if (initialData._id) res = await challengesApi.updateChallenge(initialData._id, payload);
      else res = await challengesApi.createChallenge(payload);
      onSaved && onSaved(res.challenge || res);
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally { setSubmitting(false); }
  };

  return (
    <form className="ca-form" onSubmit={handleSubmit}>
      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#7f1d1d', padding: '12px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '0.9rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          ⚠️ {error}
        </div>
      )}

      <div className="card card-large">
        <div className="card-inner">
          <label className="label">🎯 Titre du défi</label>
          <input 
            className="input" 
            value={title} 
            onChange={e => setTitle(e.target.value)}
            placeholder="Ex: 100 brasses en piscine"
            maxLength={60}
          />
          <small className="help">{title.length}/60 caractères • Doit être accrocheur et clair</small>

          <label className="label">📝 Description</label>
          <textarea 
            className="textarea" 
            value={description} 
            onChange={e => setDescription(e.target.value)}
            placeholder="Décrivez les règles, l'objectif et comment relever ce défi..."
            maxLength={500}
          />
          <small className="help">{description.length}/500 caractères • Soyez descriptif et encourageant</small>
        </div>
      </div>

      <div className="card">
        <div className="card-inner">
          <label className="label">⚽ Catégorie sportive</label>
          <select 
            className="input" 
            value={sportType} 
            onChange={e => setSportType(e.target.value)}
            style={{ cursor: 'pointer' }}
          >
            <option value="">-- Sélectionner un sport --</option>
            <option value="Football">⚽ Football</option>
            <option value="Course">🏃 Course</option>
            <option value="Natation">🏊 Natation</option>
            <option value="Basketball">🏀 Basketball</option>
            <option value="Tennis">🎾 Tennis</option>
            <option value="Cyclisme">🚴 Cyclisme</option>
            <option value="autre">🎯 Autre</option>
          </select>
          <small className="help">Choisissez la catégorie sportive</small>

          <label className="label">💪 Difficulté</label>
          <DifficultySelector value={difficulty} onChange={setDifficulty} />
          <small className="help">
            {difficulty === 'easy' && '🟢 Les utilisateurs débutants peuvent relever ce défi'}
            {difficulty === 'medium' && '🟠 Défi modéré, nécessite de l\'entraînement'}
            {difficulty === 'hard' && '🔴 Défi élite, pour les experts seulement'}
          </small>
        </div>
      </div>

      <div className="card">
        <div className="card-inner two-col">
          <div>
            <label className="label">⭐ XP récompense</label>
            <input 
              className="input" 
              type="number" 
              value={rewardXP} 
              onChange={e => setRewardXP(Number(e.target.value))}
              min={0}
              max={1000}
            />
            <small className="help">Points d'expérience (0-1000)</small>
          </div>
          <div>
            <label className="label">🎯 Valeur cible</label>
            <input 
              className="input" 
              type="number" 
              value={targetValue} 
              onChange={e => setTargetValue(Number(e.target.value))}
              min={1}
              max={10000}
            />
            <small className="help">Nombre à atteindre (ex: 100 brasses)</small>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-inner two-col">
          <div>
            <label className="label">📅 Type de défi</label>
            <select 
              className="input" 
              value={durationUnit} 
              onChange={e => setDurationUnit(e.target.value)}
              style={{ cursor: 'pointer' }}
            >
              <option value="daily">📆 Quotidien</option>
              <option value="weekly">📅 Hebdomadaire</option>
              <option value="monthly">📋 Mensuel</option>
              <option value="seasonal">🌍 Saisonnier</option>
              <option value="special">⭐ Spécial</option>
            </select>
            <small className="help">Définissez la fréquence du défi</small>
          </div>
          <div>
            <label className="label">⏱️ Disponibilité (jours)</label>
            <input 
              className="input" 
              type="number" 
              value={duration} 
              onChange={e => setDuration(Number(e.target.value))}
              min={1}
              max={365}
            />
            <small className="help">Pendant combien de jours sera-t-il disponible ?</small>
          </div>
        </div>
      </div>

      <div className="card actions-card">
        <div className="actions-inner">
          <div className="actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={submitting || !title.trim()}
            >
              {submitting ? '⏳ Enregistrement...' : '✨ Créer le défi'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

